"use client";

import type { CSSProperties } from "react";
import { SHELL_INSET_PX } from "@/components/learn-piece/shell/panel-metrics";
import { FRONT_SLOT, ROLL_WIDGET_SLOTS, slotRefs, type RollWidgetSlot } from "./roll-widget-slots";

/** Écart entre deux widgets empilés sur la même ancre (px). */
const STACK_GAP_PX = 12;

/**
 * Le coin que chaque ancre occupe. Rien d'autre : le flux est en classes.
 *
 * Le premier plan n'est pas là : ses marges changent avec l'écran, donc elles
 * s'écrivent en classes comme le flux, et un style inline l'emporterait sur
 * elles.
 */
const ANCHOR_POSITION: Readonly<Record<RollWidgetSlot, CSSProperties>> = {
  "top-left": { top: SHELL_INSET_PX, left: SHELL_INSET_PX },
  "top-center": { top: SHELL_INSET_PX, left: 0, right: 0 },
  "top-right": { top: SHELL_INSET_PX, right: SHELL_INSET_PX },
  "bottom-left": { bottom: SHELL_INSET_PX, left: SHELL_INSET_PX },
  "bottom-right": { bottom: SHELL_INSET_PX, right: SHELL_INSET_PX },
  front: {},
};

/**
 * Comment les widgets d'une même ancre s'empilent : en colonne, **en rangée sur
 * un téléphone**.
 *
 * Deux caméras empilées font 140 px de haut. Sur le canvas d'un iPhone couché,
 * rouleau seul, c'est plus de la moitié de la hauteur qu'on est venu regarder ;
 * couchées, les mêmes 140 px se prennent sur une largeur de 780 px, où ils ne
 * manquent à personne. L'écran manque de hauteur et non de largeur : la pile
 * tourne dans le sens qui reste.
 *
 * Debout, la hauteur ne manque plus — mais la largeur non plus, tant que les
 * vignettes sont des bulles : deux bulles font 140 px sur les 346 px du
 * rouleau, et couchées elles n'en mordent qu'une bande de 64 px en haut au lieu
 * d'une colonne sur toute la hauteur. La règle est donc la même dans les deux
 * orientations, pour la même raison : une rangée de bulles borde le rouleau, une
 * colonne le traverse.
 *
 * `top-center` ne tourne pas : une seule chose s'y pose, la capsule du
 * parcours, et elle est centrée.
 *
 * **En classes et non en style inline**, pour deux raisons. Une requête média
 * ne s'écrit pas dans un attribut `style` ; et le faire en JS demanderait un
 * hook de media query, qui répond `false` au premier rendu — le bureau verrait
 * passer une frame de travers. Les positions, elles, restent inline : elles
 * dérivent de constantes de code (`SHELL_INSET_PX`) que Tailwind ne sait pas
 * lire.
 *
 * Les littéraux sont écrits en entier : le scanner de Tailwind lit le source,
 * une classe construite par concaténation ne serait jamais générée.
 */
const ANCHOR_FLOW: Readonly<Record<RollWidgetSlot, string>> = {
  "top-left": "flex-col items-start justify-start handheld:flex-row",
  "top-center": "flex-col items-center justify-start",
  "top-right":
    "flex-col items-end justify-start handheld:flex-row handheld:items-start",
  // Sur les ancres du bas, le premier widget posé reste collé au bord et les
  // suivants s'empilent au-dessus — en rangée, ils s'alignent sur ce bord.
  "bottom-left":
    "flex-col items-start justify-end handheld:flex-row handheld:items-end",
  "bottom-right": "flex-col items-end justify-end handheld:flex-row",
  /*
    Le premier plan : centré, à distance des bords, et non plein cadre — un
    widget mis en avant reste un widget posé sur le rouleau, pas un écran qui
    le remplace. Les marges se resserrent sur un téléphone, où trois fois
    l'encoche du studio ne laisserait plus rien à agrandir.
  */
  front: "inset-12 handheld:inset-4 flex-col items-center justify-center",
};

/**
 * Le calque qui reçoit les widgets posés sur le roll.
 *
 * Monté une fois par `StudioFrame`, dans les mêmes bornes que le parcours par
 * cœur : au-dessus du clavier, à gauche de la minimap. Il ne dessine rien par
 * lui-même — cinq colonnes vides, une par ancre, plus le premier plan — et
 * laisse passer le pointeur : seuls les widgets le reprennent, sur leur propre
 * surface.
 *
 * Même `z-20` que les transients, et écrit après eux : à égalité c'est l'ordre
 * du DOM qui peint, donc un widget passe devant les panneaux du parcours, et
 * sous les calques qui masquent le roll (`z-24`, réglages et accords).
 *
 * Conteneur de taille : un widget posé dans un coin se borne au rouleau en
 * `cqw`/`cqh`, sans le mesurer. La hauteur du rouleau change du tout au tout
 * d'un écran à l'autre (258 px sur un téléphone couché, 700 au bureau), et
 * c'est elle, pas la fenêtre, qui dit la place qu'on peut lui prendre. Sans
 * conteneur, ces unités retombaient sur la fenêtre.
 */
export function RollWidgetLayer({
  bottom,
  right,
}: {
  /** Hauteur du clavier du roll (px). */
  readonly bottom: number;
  /** Largeur du rail de minimap (px). */
  readonly right: number;
}) {
  return (
    <div
      className="pointer-events-none absolute left-0 top-0 z-20"
      style={{ bottom, right, containerType: "size" }}
      data-roll-widget-layer
    >
      {ROLL_WIDGET_SLOTS.map((slot) => (
        <div
          key={slot}
          ref={slotRefs[slot]}
          data-roll-widget-slot={slot}
          className={`absolute flex ${ANCHOR_FLOW[slot]}`}
          style={{
            gap: STACK_GAP_PX,
            /*
              Le premier plan se borne par ses marges, pas par un plafond : il
              se donne au widget en entier. Et il est un conteneur de taille,
              pour que ce widget puisse s'y ajuster en `cqw`/`cqh` — c'est la
              seule façon de remplir une boîte en gardant un format, sans
              mesurer la boîte en JavaScript.
            */
            ...(slot === FRONT_SLOT
              ? { containerType: "size" }
              : {
                  maxWidth: `calc(100% - ${SHELL_INSET_PX * 2}px)`,
                  maxHeight: `calc(100% - ${SHELL_INSET_PX * 2}px)`,
                }),
            ...ANCHOR_POSITION[slot],
          }}
        />
      ))}
    </div>
  );
}
